<app-normal-navbar></app-normal-navbar>

<div class="site-page-wrapper">
  <!-- Soft Ambient Glow Layers -->
  <div class="site-ambient-bg"></div>

  <!-- Standardized Centered Container -->
  <div class="site-container">


    <!-- ═══════════════ HERO SECTION ═══════════════ -->
    <header class="hero-section">

      <h1 class="hero-title">Disclaimer &amp; DMCA</h1>

      <p class="hero-subtitle">
        Transparency, copyright integrity, and legal attribution are fundamental to SceneTale. This document explains
        our content licensing, TMDB API data attribution, Fair Use compliance, and our formal DMCA takedown procedures.
      </p>

      <div class="hero-meta-row">
        <span class="meta-pill"><strong>Last Reviewed:</strong> April 2026</span>
        <span class="meta-pill"><strong>Legal Standard:</strong> 17 U.S.C. &sect; 512 (DMCA)</span>
        <span class="meta-pill"><strong>Takedown SLA:</strong> Within 48 Hours</span>
      </div>
    </header>

    <!-- ═══════════════ DISCLAIMER SECTIONS ═══════════════ -->
    <div class="policy-sections-list">

      <!-- Section 1: General Disclaimer -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-emerald">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 01</span>
            <h2 class="card-title">General Platform Disclaimer</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            The information provided on <strong>SceneTale</strong> is intended for general informational, educational,
            and entertainment purposes only. While our editorial team makes every effort to verify cinematic trivia,
            release schedules, and historical data, we make no warranties or representations regarding absolute
            completeness, timeliness, or accuracy.
          </p>
          <p>
            Any reliance you place on editorial reviews, opinion columns, or ratings found on this platform is strictly
            at your own discretion.
          </p>
        </div>
      </section>

      <!-- Section 2: TMDB Attribution (Crucial for AdSense & TMDB Terms) -->
      <section class="policy-card featured-policy-card">
        <div class="card-header-row">
          <div class="card-icon-box icon-gold">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
              <line x1="7" y1="2" x2="7" y2="22"></line>
              <line x1="17" y1="2" x2="17" y2="22"></line>
              <line x1="2" y1="12" x2="22" y2="12"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag tag-gold">Official API Attribution</span>
            <h2 class="card-title">The Movie Database (TMDB) Attribution</h2>
          </div>
        </div>
        <div class="card-body">
          <div class="legal-highlight-box">
            <h4>TMDB Compliance Declaration:</h4>
            <p>
              <strong>This product uses the TMDB API but is not endorsed or certified by TMDB.</strong>
            </p>
            <ul>
              <li>
                All movie titles, theatrical posters, television backdrops, actor profiles, synopsis summaries, and
                release dates displayed throughout SceneTale are retrieved via the TMDB API.
              </li>
              <li>
                All visual materials, promotional banners, and studio intellectual properties belong entirely to their
                respective copyright holders, film production studios, and distributors.
              </li>
              <li>
                SceneTale claims zero ownership or exclusive licensing rights over the film promotional materials
                supplied through TMDB.
              </li>
            </ul>
          </div>
        </div>
      </section>

      <!-- Section 3: Fair Use Notice -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-teal">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 03</span>
            <h2 class="card-title">Fair Use Notice (17 U.S.C. &sect; 107)</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            SceneTale contains copyrighted material the use of which has not always been specifically authorized by the
            copyright owner. We make such material available under the doctrine of <strong>Fair Use</strong> for
            purposes including:
          </p>
          <div class="grid-two-col">
            <div class="feature-box">
              <h4>🎞️ Film Criticism &amp; Review</h4>
              <p>In-depth analysis of cinematography, directorial techniques, and narrative themes in our Cinephile
                Journal.</p>
            </div>
            <div class="feature-box">
              <h4>🧠 Transformative Cinema Trivia</h4>
              <p>Gamified educational quizzes that challenge film literacy and preserve cinema history.</p>
            </div>
            <div class="feature-box">
              <h4>📅 Cultural Commentary</h4>
              <p>Documenting cinematic milestones, festival retrospectives, and box-office historical context.</p>
            </div>
            <div class="feature-box">
              <h4>🔍 Public Indexing</h4>
              <p>Facilitating discovery of classic and independent world cinema for moviegoers.</p>
            </div>
          </div>
          <p>
            We believe this constitutes a "fair use" of any such copyrighted material as provided for in section 107 of
            the US Copyright Law.
          </p>
        </div>
      </section>

      <!-- Section 4: Advertising & Google AdSense -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-amber">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="4"></rect>
              <line x1="8" y1="8" x2="16" y2="16"></line>
              <line x1="16" y1="8" x2="8" y2="16"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 04</span>
            <h2 class="card-title">Advertising &amp; Google AdSense Disclosure</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            To keep our cinema guides, trivia games, and journals free and accessible to all film lovers, SceneTale
            accepts online advertising from networks including <strong>Google AdSense</strong>.
          </p>
          <ul class="styled-list">
            <li>
              Advertisements are clearly labeled by the respective ad network (e.g., "Ads by Google").
            </li>
            <li>
              The presence of an advertisement on SceneTale does not constitute an endorsement, recommendation, or
              warranty of the third-party merchant, product, or service being advertised.
            </li>
            <li>
              SceneTale maintains strict editorial independence. Advertisers and sponsors have zero control over our
              film ratings, quiz solutions, or blog articles.
            </li>
          </ul>
        </div>
      </section>

      <!-- Section 5: DMCA Takedown Procedure -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-emerald">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 05</span>
            <h2 class="card-title">DMCA Takedown Notice &amp; Filing Procedure</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            SceneTale complies with the Digital Millennium Copyright Act (17 U.S.C. &sect; 512). If you are a copyright
            owner or authorized agent and believe that content hosted on SceneTale infringes your rights, please submit
            a written DMCA notice containing:
          </p>
          <ul class="styled-list">
            <li>A physical or electronic signature of a person authorized to act on behalf of the copyright owner.</li>
            <li>Identification of the copyrighted work claimed to have been infringed.</li>
            <li>Identification of the material that is claimed to be infringing, with precise URLs or locations on
              SceneTale.</li>
            <li>Your contact information (legal name, address, phone number, and verified email address).</li>
            <li>A statement that you have a good faith belief that use of the material in the manner complained of is
              not authorized by the copyright owner, its agent, or the law.</li>
            <li>A statement that the information in the notification is accurate, and under penalty of perjury, that you
              are authorized to act on behalf of the owner.</li>
          </ul>
        </div>
      </section>

      <!-- Section 6: Designated DMCA Agent Contact -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-teal">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 06</span>
            <h2 class="card-title">Designated Copyright Agent</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            All formal copyright inquiries and DMCA takedown requests should be routed directly to our designated
            copyright officer:
          </p>
          <div class="contact-pill-row">
            <a href="mailto:dmca@scenetale.com" class="contact-pill-link">
              <span class="pill-dot pill-dot--amber"></span>
              <strong>DMCA Agent:</strong> dmca&#64;scenetale.com
            </a>
            <a href="mailto:contact@scenetale.com" class="contact-pill-link">
              <span class="pill-dot"></span>
              <strong>General Support:</strong> contact&#64;scenetale.com
            </a>
          </div>
          <p class="highlight-callout">
            <strong>Prompt Turnaround:</strong> Our legal team acknowledges all legitimate takedown notices within 24 to
            48 business hours and acts swiftly to remove or disable access to verified infringing materials.
          </p>
        </div>
      </section>

    </div>

    <!-- ═══════════════ BOTTOM TRUST BANNER ═══════════════ -->
    <section class="bottom-trust-card">
      <div class="trust-content">
        <h3>Committed to Creator Rights</h3>
        <p>Explore our complete legal and privacy policies to see how we maintain standard compliance.</p>
        <div class="trust-actions">
          <a routerLink="/privacy-policy" class="btn-primary-pill">View Privacy Policy</a>
          <a routerLink="/about" class="btn-secondary-pill">About SceneTale &rarr;</a>
        </div>
      </div>
    </section>

  </div>
</div>

<app-footer></app-footer>